import { useState, useEffect, useCallback, useMemo } from 'react';

import { BREAKPOINTS, type BreakpointKey } from '@/types/layout';

// =============================================================================
// TYPES
// =============================================================================

export interface UseBreakpointReturn {
  /** Current window width in pixels */
  width: number;
  /** Current breakpoint key based on width */
  breakpoint: BreakpointKey | 'xs';
  /** Check if viewport is at or above the breakpoint */
  isAbove: (bp: BreakpointKey) => boolean;
  /** Check if viewport is below the breakpoint */
  isBelow: (bp: BreakpointKey) => boolean;
  /** Check if viewport is within the breakpoint range */
  isAt: (bp: BreakpointKey) => boolean;
  /** Check if viewport is between two breakpoints (min inclusive, max exclusive) */
  isBetween: (min: BreakpointKey, max: BreakpointKey) => boolean;
}

// Ordered breakpoint keys for isAt calculation
const BREAKPOINT_ORDER: BreakpointKey[] = ['sm', 'md', 'lg', 'xl'];

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for responsive breakpoint detection aligned with Tailwind CSS.
 *
 * Provides size-based breakpoint functions instead of device-based booleans:
 * - `isAbove(bp)`: width >= BREAKPOINTS[bp]
 * - `isBelow(bp)`: width < BREAKPOINTS[bp]
 * - `isAt(bp)`: within the breakpoint range
 * - `isBetween(min, max)`: between two breakpoints
 *
 * @example
 * ```tsx
 * const { isAbove, isBelow, width } = useBreakpoint();
 *
 * // Matches Tailwind's md: prefix (>= 768px)
 * const showSidebar = isAbove('md');
 *
 * // Matches Tailwind's max-md: (< 768px)
 * const showMobileNav = isBelow('md');
 *
 * // Between md and lg (768-1023px)
 * const { isBetween } = useBreakpoint();
 * const isTabletOnly = isBetween('md', 'lg');
 * ```
 */
export function useBreakpoint(): UseBreakpointReturn {
  const [width, setWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return BREAKPOINTS.lg; // Default to lg for SSR
  });

  const handleResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const isAbove = useCallback(
    (bp: BreakpointKey) => width >= BREAKPOINTS[bp],
    [width]
  );

  const isBelow = useCallback(
    (bp: BreakpointKey) => width < BREAKPOINTS[bp],
    [width]
  );

  const isAt = useCallback(
    (bp: BreakpointKey) => {
      const idx = BREAKPOINT_ORDER.indexOf(bp);
      const nextBp = BREAKPOINT_ORDER[idx + 1];
      const minWidth = BREAKPOINTS[bp];
      const maxWidth = nextBp ? BREAKPOINTS[nextBp] : Infinity;
      return width >= minWidth && width < maxWidth;
    },
    [width]
  );

  const isBetween = useCallback(
    (min: BreakpointKey, max: BreakpointKey) =>
      width >= BREAKPOINTS[min] && width < BREAKPOINTS[max],
    [width]
  );

  const breakpoint = useMemo((): BreakpointKey | 'xs' => {
    if (width >= BREAKPOINTS.xl) return 'xl';
    if (width >= BREAKPOINTS.lg) return 'lg';
    if (width >= BREAKPOINTS.md) return 'md';
    if (width >= BREAKPOINTS.sm) return 'sm';
    return 'xs';
  }, [width]);

  return useMemo(
    () => ({
      width,
      breakpoint,
      isAbove,
      isBelow,
      isAt,
      isBetween,
    }),
    [width, breakpoint, isAbove, isBelow, isAt, isBetween]
  );
}

// =============================================================================
// Re-export breakpoint constants for convenience
// =============================================================================

export { BREAKPOINTS, type BreakpointKey } from '@/types/layout';
